import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { MOBILE_MAX_WIDTH } from "../../constants/breakpoints";
import { getBeautySalonApi } from "../../api/api";
import SalonDetailDesktop from "./SalonDetailDesktop";
import SalonDetailMobile from "./SalonDetailMobile";
import BeautyBookingMobile from "./BeautyBookingMobile";
import BeautyBookingDesktop, { DESKTOP_STEP_DATA, DESKTOP_STEP_TURN } from "./BeautyBookingDesktop";
import { LinkedProfessionalCard } from "./components/BookingParts";
import { useBeautyBookingFlow } from "./useBeautyBookingFlow";
import { useSedeActiva } from "../reservas/components/useSedeActiva";
import { isOnlineBookingDisabled } from "../reservas/onlineBooking";
import "../reservas/reservas.css";
import "./beauty.css";

const normalizeSalonDetail = (response) => {
  const info = response?.data?.info || {};
  return {
    salon: info.salon || null,
    services: Array.isArray(info.services) ? info.services : [],
    professionals: Array.isArray(info.professionals) ? info.professionals : [],
    sucursales: Array.isArray(info.sucursales) ? info.sucursales : [],
    casaCentral: info.casaCentral || null,
  };
};

/**
 * Owns the booking flow once the salon is loaded. Mobile opens the 6-step
 * wizard from "Reservar turno" (or resumes a live hold after a reload). Desktop
 * keeps the salon page as step 1 and swaps in "Elegir turno" / "Tus datos".
 */
const SalonBooking = ({ data: fullData, isMobile, professionalSlug, onLeaveLink, heroSalon = null, sedes = null }) => {
  // Personal link (/beauty/:salonRef/:profesionalSlug): the professional comes
  // preselected and only their services are listed. An unknown slug falls back
  // to the normal flow with a notice.
  const linkedProfessional = useMemo(
    () =>
      professionalSlug
        ? fullData.professionals.find((p) => p.slug && String(p.slug).toLowerCase() === String(professionalSlug).toLowerCase()) || null
        : null,
    [fullData.professionals, professionalSlug],
  );
  const unknownSlug = Boolean(professionalSlug) && !linkedProfessional;
  const data = useMemo(() => {
    if (!linkedProfessional) return fullData;
    const allowed = new Set((linkedProfessional.serviceIds || []).map(String));
    return { ...fullData, services: fullData.services.filter((s) => allowed.has(String(s._id))) };
  }, [fullData, linkedProfessional]);
  const flow = useBeautyBookingFlow(data, {
    linkedProfessionalId: linkedProfessional ? linkedProfessional.id : null,
    onLeaveLink,
  });
  // Where the booking resumes: a live hold opens Tus datos / Resumen, a selection
  // restored after login opens Elegir turno / Horario. null = follow it, else the
  // buyer's own navigation wins.
  const resumed = flow.restoredStep === "resumen" ? "data" : flow.restoredStep === "slots" ? "turn" : null;
  const [wizardState, setWizardOpen] = useState(null);
  const [stepState, setDesktopStep] = useState(null);
  const wizardOpen = wizardState ?? Boolean(resumed);
  // Desktop step: 1 = salon page, 2 = Elegir turno, 3 = Tus datos.
  const desktopStep = stepState ?? (resumed === "data" ? DESKTOP_STEP_DATA : resumed === "turn" ? DESKTOP_STEP_TURN : 1);
  const startBooking = () => {
    if (isOnlineBookingDisabled(data.salon)) return; // the layouts disable the CTA; this is the safety net
    return isMobile ? setWizardOpen(true) : setDesktopStep(DESKTOP_STEP_TURN);
  };

  // Re-creating the hold after the login redirect.
  if (flow.restoredStep === "pending") {
    return (
      <div className="reservas-root">
        <div className="wrap beauty-resuming" role="status">
          <Spinner animation="border" />
          <p>Estamos retomando tu turno…</p>
        </div>
      </div>
    );
  }

  if (isMobile && wizardOpen) {
    return (
      <BeautyBookingMobile
        salon={data.salon}
        services={data.services}
        professionals={data.professionals}
        flow={flow}
        onExit={() => setWizardOpen(false)}
      />
    );
  }

  if (!isMobile && desktopStep > 1) {
    return <BeautyBookingDesktop salon={data.salon} flow={flow} step={desktopStep} onStepChange={setDesktopStep} />;
  }

  const topSlot = linkedProfessional ? (
    <LinkedProfessionalCard flow={flow} />
  ) : unknownSlug ? (
    <Alert variant="warning" role="status">No encontramos a ese profesional en este salón. Elegí con quién reservar.</Alert>
  ) : null;

  const Layout = isMobile ? SalonDetailMobile : SalonDetailDesktop;
  return (
    <Layout
      topSlot={topSlot}
      salon={data.salon}
      services={data.services}
      professionals={data.professionals}
      sucursales={data.sucursales}
      casaCentral={data.casaCentral}
      heroSalon={heroSalon}
      sedes={sedes}
      onStartBooking={startBooking}
      {...(isMobile ? {} : { cart: flow.cartServices, choices: flow.cartChoices, onAdd: flow.addService, onUpdate: flow.updateService, onRemove: flow.removeService })}
    />
  );
};

/**
 * Public beauty salon page shell. Fetches the salon by slug or id and switches
 * between the desktop and mobile layouts, mirroring ClubDetail.jsx.
 */
const SalonDetail = () => {
  const { salonRef, profesionalSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });

  const [data, setData] = useState({ salon: null, services: [], professionals: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    if (!salonRef) return undefined;

    const loadSalon = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getBeautySalonApi(salonRef);
        if (!active) return;
        const normalized = normalizeSalonDetail(response);
        setData(normalized);
        if (!normalized.salon) setError("No pudimos encontrar el local.");
      } catch (err) {
        if (!active) return;
        setData({ salon: null, services: [], professionals: [] });
        setError(err?.response?.data?.message || "No pudimos encontrar el local.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSalon();
    return () => { active = false; };
  }, [salonRef, reloadKey]);

  // Casa central: the sede in `?sucursal=<slug|id>` swaps the bookable content in place.
  const fetchSedeDetail = useCallback(async (ref) => {
    const normalized = normalizeSalonDetail(await getBeautySalonApi(ref));
    return normalized.salon ? normalized : null;
  }, []);
  const { sedes, entry, status: sedeStatus } = useSedeActiva({ casa: data.salon, sucursales: data.sucursales, fetchDetail: fetchSedeDetail });

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);
  // Leaving the personal link keeps the chosen sede (`?sucursal=`).
  const leaveLink = useCallback(() => navigate(`/beauty/${salonRef}${location.search}`), [navigate, salonRef, location.search]);

  if (loading) {
    return (
      <div className="reservas-root">
        <div className="wrap" style={{ textAlign: "center", padding: "80px 0" }}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (!data.salon) {
    return (
      <div className="reservas-root">
        <div className="wrap">
          <Alert variant="danger">{error || "No pudimos encontrar el local."}</Alert>
          <div className="beauty-error-actions">
            <button type="button" className="r-btn-primary" onClick={retry}>
              Reintentar
            </button>
            <button type="button" className="r-btn-soft" onClick={() => navigate("/beauty")}>
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  // The chosen sede is still loading (or failed): casa central hero and Sucursales, no booking flow yet.
  if (sedeStatus) {
    const Layout = isMobile ? SalonDetailMobile : SalonDetailDesktop;
    return (
      <Layout
        salon={data.salon}
        services={[]}
        professionals={[]}
        sucursales={data.sucursales}
        sedes={sedes}
        sedeStatus={sedeStatus}
      />
    );
  }

  // On a sede the booking runs on that sede's salon (its id reaches availability, holds and reservations).
  const bookData = entry
    ? { ...entry.data, sucursales: data.sucursales, casaCentral: null }
    : data;
  return (
    <SalonBooking
      key={`${profesionalSlug || "normal"}:${bookData.salon._id}`}
      data={bookData}
      heroSalon={entry ? data.salon : null}
      sedes={sedes}
      isMobile={isMobile}
      professionalSlug={profesionalSlug}
      onLeaveLink={leaveLink}
    />
  );
};

export default SalonDetail;
